// frontend/src/api/arthouseApi.js

const API_BASE = process.env.REACT_APP_API_BASE || "";

/**
 * DEV ONLY:
 * - Only send x-user-id in development
 * - Never default to "1"
 */
function getDevUserIdHeader() {
  if (process.env.NODE_ENV !== "development") return {};
  const id = localStorage.getItem("DEV_USER_ID");
  return id ? { "x-user-id": id } : {};
}

/**
 * OPTIONAL:
 * If your app ever stores an access token (NOT required for cookie auth),
 * we attach it. This does nothing unless you actually set one of these keys.
 */
function getAuthHeader() {
  const keys = ["accessToken", "token", "jwt", "authToken"];

  let token = null;
  try {
    for (const k of keys) {
      token = localStorage.getItem(k);
      if (token) break;
    }
    if (!token) {
      for (const k of keys) {
        token = sessionStorage.getItem(k);
        if (token) break;
      }
    }
  } catch {
    // ignore
  }

  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Transition normalizer (HARDENED):
 * - absolute URLs => untouched
 * - "/api/..." => stays
 * - "/api/api/..." => collapses to "/api/..." (fixes your bug)
 * - "/arthouse/..." => becomes "/api/arthouse/..."
 * - any other "/something" => becomes "/api/something"
 */
function normalizePath(path) {
  const raw = String(path || "");

  // absolute URL => don't touch
  if (/^https?:\/\//i.test(raw)) return raw;

  // ensure leading slash
  const p = raw.startsWith("/") ? raw : `/${raw}`;

  // fix the exact bad path you showed: "/api/api/..."
  if (p.startsWith("/api/api/")) return p.replace(/^\/api\/api\//, "/api/");

  // already includes /api
  if (p.startsWith("/api/")) return p;

  // allow arthouse shorthand
  if (p.startsWith("/arthouse/")) return `/api${p}`;

  // default: treat as API route
  return `/api${p}`;
}

async function apiFetch(path, options = {}) {
  const normalized = normalizePath(path);
  const url =
    /^https?:\/\//i.test(normalized) ? normalized : `${API_BASE}${normalized}`;

  const method = (options.method || "GET").toUpperCase();
  const hasBody = options.body != null;
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;

  const headers = {
    ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
    ...getDevUserIdHeader(),
    ...getAuthHeader(),
  };

  const res = await fetch(url, {
    credentials: "include", // required for cookie auth
    ...options,
    method,
    headers,
  });

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const msg =
      typeof data === "string"
        ? data
        : data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    err.path = normalized;
    throw err;
  }

  return data;
}

/* -------------------------------------------
   Local cache keys (used by flow)
-------------------------------------------- */
const HAS_PROFILE_KEY = "ARTHOUSE_HAS_PROFILE";
const MY_ARTIST_ID_KEY = "ARTHOUSE_MY_ARTIST_ID";

/* -------------------------------------------
   Cache helpers (exported for UI)
-------------------------------------------- */
function setArtistCache(artistId) {
  if (!artistId) return;
  try {
    localStorage.setItem(HAS_PROFILE_KEY, "1");
    localStorage.setItem(MY_ARTIST_ID_KEY, String(artistId));
  } catch {
    // ignore
  }
}

export function getCachedMyArtistId() {
  try {
    const raw = localStorage.getItem(MY_ARTIST_ID_KEY);
    if (!raw) return null;
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) ? n : raw; // allow UUID future
  } catch {
    return null;
  }
}

export function hasCachedArtistProfile() {
  try {
    return localStorage.getItem(HAS_PROFILE_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearArtistCache() {
  try {
    localStorage.removeItem(HAS_PROFILE_KEY);
    localStorage.removeItem(MY_ARTIST_ID_KEY);
  } catch {
    // ignore
  }
}

/* -------------------------------------------
   Helpers: normalize shapes from backend
-------------------------------------------- */
function normalizeArtistResponse(data) {
  if (!data) return { artist: null };

  const artist =
    data.artist ||
    data?.data?.artist ||
    data.profile ||
    data?.data?.profile ||
    null;

  if (!artist || typeof artist !== "object" || Array.isArray(artist)) {
    return { ...data, artist: null };
  }

  return { ...data, artist };
}

function cacheArtistIfPossible(artist) {
  const id = artist?.id ?? artist?.artist_id;
  if (id) setArtistCache(id);
}

function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

/**
 * Normalize artwork detail so UI ALWAYS gets:
 * { artwork, tags, merchLinks }
 */
function normalizeArtworkDetailResponse(data) {
  if (!data) return { artwork: null, tags: [], merchLinks: [] };

  if (data.artwork && typeof data.artwork === "object") {
    return {
      ...data,
      artwork: data.artwork,
      tags: safeArr(data.tags).length ? data.tags : safeArr(data.artwork?.tags),
      merchLinks: safeArr(data.merchLinks).length
        ? data.merchLinks
        : safeArr(data.artwork?.merchLinks).length
        ? data.artwork.merchLinks
        : safeArr(data.merch_links).length
        ? data.merch_links
        : safeArr(data.artwork?.merch_links),
    };
  }

  const artwork = data;
  return {
    artwork,
    tags: safeArr(artwork?.tags),
    merchLinks: safeArr(artwork?.merchLinks).length
      ? artwork.merchLinks
      : safeArr(artwork?.merch_links),
  };
}


/* -------------------------------------------
   PUBLIC
-------------------------------------------- */
export function fetchArtworks({
  sort = "new",
  q = "",
  tag = "",
  page = 1,
  limit = 24,
} = {}) {
  const params = new URLSearchParams();
  params.set("sort", sort);
  if (q) params.set("q", q);
  if (tag) params.set("tag", tag);
  params.set("page", String(page));
  params.set("limit", String(limit));

  // arthouse shorthand supported
  return apiFetch(`/arthouse/artworks?${params.toString()}`);
}

export function fetchAvailableTags() {
  return apiFetch(`/arthouse/tags`);
}

export async function fetchArtworkDetail(artworkId) {
  const raw = await apiFetch(`/arthouse/artworks/${artworkId}`);
  return normalizeArtworkDetailResponse(raw);
}

export function fetchArtistProfile(artistId) {
  return apiFetch(`/arthouse/artists/${artistId}`);
}

/* -------------------------------------------
   CREATOR (dashboard/edit endpoints)
-------------------------------------------- */
export async function fetchMyArtistProfile() {
  const raw = await apiFetch(`/arthouse/me/profile`);
  const normalized = normalizeArtistResponse(raw);

  if (normalized?.artist && !(normalized.artist.id ?? normalized.artist.artist_id)) {
    try {
      const resolved = await fetchMyArtist();
      return resolved;
    } catch {
      return normalized;
    }
  }

  cacheArtistIfPossible(normalized?.artist);
  return normalized;
}

export async function updateMyArtistProfile(payload) {
  const raw = await apiFetch(`/arthouse/me/profile`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  const normalized = normalizeArtistResponse(raw);
  cacheArtistIfPossible(normalized?.artist);
  return normalized;
}

export function fetchMyArtworks() {
  return apiFetch(`/arthouse/me/artworks`);
}

export async function fetchMyArtworkDetail(artworkId) {
  const raw = await apiFetch(`/arthouse/me/artworks/${artworkId}`);
  return normalizeArtworkDetailResponse(raw);
}

function devLogMerchPayload(payload) {
  if (process.env.NODE_ENV !== "development") return;

  const arr =
    payload?.merchLinks ||
    payload?.merch_links ||
    payload?.merch_links_array ||
    payload?.merch ||
    null;

  if (!arr) return;

  try {
    console.log(
      "[createArtwork] merch links type =",
      Array.isArray(arr) ? "array" : typeof arr
    );
    console.log(
      "[createArtwork] merch links length =",
      Array.isArray(arr) ? arr.length : "n/a"
    );
    console.log(
      "[createArtwork] merch links sample =",
      Array.isArray(arr) ? arr.slice(0, 5) : arr
    );
  } catch {
    // ignore
  }
}

export function createArtwork(payload) {
  devLogMerchPayload(payload);

  return apiFetch(`/arthouse/me/artworks`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateMyArtwork(artworkId, payload) {
  devLogMerchPayload(payload);

  return apiFetch(`/arthouse/me/artworks/${artworkId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteMyArtwork(artworkId) {
  return apiFetch(`/arthouse/me/artworks/${artworkId}`, {
    method: "DELETE",
  });
}

export async function fetchMyArtist() {
  // Preferred route
  try {
    const raw1 = await apiFetch(`/arthouse/me/artist`);
    const d1 = normalizeArtistResponse(raw1);

    if (d1?.artist && (d1.artist.id ?? d1.artist.artist_id)) {
      cacheArtistIfPossible(d1.artist);
      return d1;
    }
  } catch {
    // fallback below
  }

  // Fallback
  const raw2 = await apiFetch(`/arthouse/me/profile`);
  const d2 = normalizeArtistResponse(raw2);

  if (!d2?.artist || !(d2.artist.id ?? d2.artist.artist_id)) {
    const err = new Error("Artist not found");
    err.status = 404;
    err.data = d2;
    throw err;
  }

  cacheArtistIfPossible(d2.artist);
  return d2;
}

/* -------------------------------------------
   IMAGE UPLOADS (R2-backed)
   - DO NOT set Content-Type manually for FormData
   - Field name MUST be "image"
-------------------------------------------- */

function ensureFile(file) {
  if (!file) throw new Error("file is required");
}

function ensureArtworkId(artworkId) {
  if (!artworkId) throw new Error("artworkId is required");
}

export async function uploadMyArtworkImage(artworkId, file) {
  ensureArtworkId(artworkId);
  ensureFile(file);

  const fd = new FormData();
  fd.append("image", file);

  return apiFetch(`/arthouse/me/artworks/${artworkId}/image`, {
    method: "POST",
    body: fd,
  });
}

export async function uploadMyAvatarImage(file) {
  ensureFile(file);

  const fd = new FormData();
  fd.append("image", file);

  return apiFetch(`/arthouse/me/avatar`, {
    method: "POST",
    body: fd,
  });
}

export async function uploadMyBannerImage(file) {
  ensureFile(file);

  const fd = new FormData();
  fd.append("image", file);

  return apiFetch(`/arthouse/me/banner`, {
    method: "POST",
    body: fd,
  });
}

export async function uploadMyProfileBgImage(file) {
  ensureFile(file);

  const fd = new FormData();
  fd.append("image", file);

  return apiFetch(`/arthouse/me/profile-bg`, {
    method: "POST",
    body: fd,
  });
}

export function deleteMyArtworkImage(artworkId) {
  ensureArtworkId(artworkId);
  return apiFetch(`/arthouse/me/artworks/${artworkId}/image`, {
    method: "DELETE",
  });
}

export function deleteMyAvatarImage() {
  return apiFetch(`/arthouse/me/avatar`, {
    method: "DELETE",
  });
}

export function deleteMyBannerImage() {
  return apiFetch(`/arthouse/me/banner`, {
    method: "DELETE",
  });
}

export function deleteMyProfileBgImage() {
  return apiFetch(`/arthouse/me/profile-bg`, {
    method: "DELETE",
  });
}
